import { addDays, differenceInDays, isBefore } from 'date-fns';
import { InvoicePaymentDisplayStatusType } from 'anchor-scout-common/types';
import { Accounting, InvoicePaymentMethodStatus } from '~/types/api.model';
import { ApprovalMethodNoticePeriodType } from '../approval-method-notice-period';
import { InvoiceStatus } from './model';
export function isPending(invoice) {
    return invoice.status.type === InvoiceStatus.Pending;
}
export function isPaid(invoice) {
    return invoice.status.type === InvoiceStatus.Paid;
}
export function isConfirmed(invoice) {
    return invoice.status.type === InvoiceStatus.PaymentConfirmed;
}
export function isProcessing(invoice) {
    return invoice.status.type === InvoiceStatus.ProcessingCharge;
}
export function isPaidExternally(invoice) {
    return invoice.status.type === InvoiceStatus.PaidExternally;
}
export function isVoided(invoice) {
    return invoice.status.type === InvoiceStatus.Voided;
}
export function isRefunded(invoice) {
    return invoice.status.type === InvoiceStatus.Refunded;
}
export function isPartiallyRefunded(invoice) {
    return invoice.status.type === InvoiceStatus.PartiallyRefunded;
}
export function totalRefunded(invoice) {
    var _invoice_payment;
    return ((_invoice_payment = invoice.payment) === null || _invoice_payment === void 0 ? void 0 : _invoice_payment.totalRefunded()) || '0';
}
export function hasRefundFees(invoice) {
    var _invoice_payment;
    return ((_invoice_payment = invoice.payment) === null || _invoice_payment === void 0 ? void 0 : _invoice_payment.hasRefundFees()) || false;
}
export function isPaymentStopped(invoice) {
    return invoice.status.type === InvoiceStatus.PaymentStopped;
}
export function isPaymentFailed(invoice) {
    return invoice.status.type === InvoiceStatus.PaymentFailed;
}
export function isRequiresPaymentApproval(invoice) {
    return invoice.status.type === InvoiceStatus.RequiresPaymentApproval;
}
export function isPaymentActionRequired(invoice) {
    return invoice.status.type === InvoiceStatus.PaymentActionRequired;
}
export function isDisputed(invoice) {
    return invoice.status.type === InvoiceStatus.PaymentDisputed;
}
export function isRefundRequested(paymentStatus) {
    return !!paymentStatus && paymentStatus.type === InvoicePaymentDisplayStatusType.RefundRequested;
}
export function isRefundFailed(paymentStatus) {
    return !!paymentStatus && paymentStatus.type === InvoicePaymentDisplayStatusType.RefundFailed;
}
export function statusType(invoice) {
    return invoice.status.type;
}
export function paymentMethodStatus(invoice) {
    return invoice.paymentMethodStatus;
}
export function disputedMessage(invoice) {
    return invoice.status.type === InvoiceStatus.PaymentDisputed ? invoice.status.paymentDisputed.message : null;
}
export function dueDate(invoice) {
    return invoice.dueDate;
}
export function formattedNumber(param) {
    let { formattedNumber } = param;
    return formattedNumber;
}
export function customerName(inv) {
    return inv.relationship.customer.name;
}
export function issuedOn(param) {
    let { issuedOn } = param;
    return issuedOn;
}
export function issueDate(invoice) {
    return invoice.issuedOn || invoice.createdOn;
}
//in case user billed now with a past issue date, we should use the createdOn date
export function paymentProcessingDate(invoice) {
    return isBefore(invoice.rawDueDate, invoice.rawCreatedOn) ? invoice.createdOn : invoice.dueDate;
}
export function paymentFailureMessage(param) {
    let { status } = param;
    return status.type === InvoiceStatus.PaymentFailed ? status.paymentFailed.message : undefined;
}
export function paymentFailureCode(param) {
    let { status } = param;
    return status.type === InvoiceStatus.PaymentFailed ? status.paymentFailed.failureCode : undefined;
}
export function processingAction(param) {
    let { status } = param;
    if (status.type === InvoiceStatus.PaymentActionRequired) {
        const { processingAction } = status.paymentActionRequired;
        return {
            stripeUrl: processingAction.url,
            clientSecret: processingAction.clientSecret
        };
    } else {
        return {
            stripeUrl: null,
            clientSecret: null
        };
    }
}
export function isPaymentMethodUnset(invoice) {
    return invoice.paymentMethodStatus === InvoicePaymentMethodStatus.PaymentMethodNotSet;
}
export function isPaymentMethodUnverified(invoice) {
    return invoice.paymentMethodStatus === InvoicePaymentMethodStatus.PaymentMethodUnverified;
}
export function isStableIntegrationStatus(invoice) {
    const trueFnc = ()=>true;
    return invoice.integration.accept({
        whenPendingSync: trueFnc,
        whenInvoiceSyncFailed: trueFnc,
        whenPaymentSyncFailed: trueFnc,
        whenRefundSyncFailed: trueFnc,
        whenVoidedRecorded: trueFnc,
        whenPaymentRecorded: trueFnc,
        whenRefundRecorded: trueFnc,
        whenInvoiceRecorded: ()=>isPending(invoice) || isPaidExternally(invoice) || isPaymentStopped(invoice) || isPaid(invoice) && invoice.isTotalAmountZero || isRequiresPaymentApproval(invoice) || isPaymentActionRequired(invoice)
    });
}
export const qboIntegration = (param)=>{
    let { integration } = param;
    return integration;
};
export const xeroIntegration = (param)=>{
    let { xeroIntegration } = param;
    return xeroIntegration.getIntegration();
};
export function paymentStopReason(invoice) {
    return invoice.status.type === InvoiceStatus.PaymentStopped ? invoice.status.paymentStopped.reason : '';
}
export const autoApprovalDate = (invoice)=>{
    const { status, createdOn } = invoice;
    const approvalMethod = status.type === InvoiceStatus.RequiresPaymentApproval ? status.requiresPaymentApproval.approvalMethod : null;
    return approvalMethod && approvalMethod.type === ApprovalMethodNoticePeriodType.Automatic ? addDays(new Date(createdOn), approvalMethod.auto.noticePeriodDays) : null;
};
export const totalAmount = (param)=>{
    let { totalAmount } = param;
    return totalAmount;
};
export const id = (param)=>{
    let { id } = param;
    return id;
};
export const itemName = (param)=>{
    let { name } = param;
    return name;
};
export const itemQuantity = (param)=>{
    let { quantity } = param;
    return quantity || '';
};
export const itemRate = (param)=>{
    let { rate } = param;
    return rate;
};
export const itemDiscount = (param)=>{
    let { discount } = param;
    return discount;
};
export const itemTotalAmount = (param)=>{
    let { totalAmount } = param;
    return totalAmount;
};
export const subItemName = (param)=>{
    let { name } = param;
    return name;
};
export const subItemRate = (param)=>{
    let { rate } = param;
    return rate;
};
export const subItemDiscount = (param)=>{
    let { discount } = param;
    return discount;
};
export const subItemTotalAmount = (param)=>{
    let { totalAmount } = param;
    return totalAmount;
};
export const subItemUnitName = (param)=>{
    let { unitName } = param;
    return unitName;
};
export const vendorName = (inv)=>inv.relationship.vendor.name;
export const otherPartyCompanyName = (inv, isVendor)=>isVendor ? customerName(inv) : vendorName(inv);
export const relationshipId = (inv)=>inv.relationship.id;
export const relationshipTitle = (inv)=>inv.relationship.title;
export function isSynced(inv) {
    return inv.integration.isSynced();
}
export function isNotSynced(inv) {
    return !!inv.integration && !inv.integration.isSynced();
}
export const groupedLineItems = (item)=>item.groupedLineItems;
export const isGrouped = (item)=>groupedLineItems(item).length > 0;
export const rawTotalAmount = (param)=>{
    let { rawTotalAmount } = param;
    return rawTotalAmount;
};
export const accounting = (param)=>{
    let { accounting, invoice } = param;
    switch(accounting){
        case Accounting.QBO:
            return {
                integration: qboIntegration(invoice)
            };
        case Accounting.Xero:
            return {
                integration: xeroIntegration(invoice)
            };
    }
};
export const payment = (param)=>{
    let { payment } = param;
    return payment;
};
export const numRefunds = (invoice)=>{
    var _ref;
    var _invoice_payment_refunds, _invoice_payment;
    return (_ref = (_invoice_payment = invoice.payment) === null || _invoice_payment === void 0 ? void 0 : (_invoice_payment_refunds = _invoice_payment.refunds) === null || _invoice_payment_refunds === void 0 ? void 0 : _invoice_payment_refunds.length) !== null && _ref !== void 0 ? _ref : null;
};
export const daysSincePayment = (invoice)=>{
    if (invoice.status.type === InvoiceStatus.Paid) {
        const paidTimestamp = invoice.status.paid.paymentDate;
        return differenceInDays(new Date().setHours(0, 0, 0, 0), new Date(paidTimestamp).setHours(0, 0, 0, 0));
    } else {
        return null;
    }
};
